import React, { useEffect, useState } from 'react';
import { Box, Typography } from '@mui/material';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import NotificationsNoneIcon from '@mui/icons-material/NotificationsNone';
import PeopleIcon from '@mui/icons-material/People';
import AssignmentIcon from '@mui/icons-material/Assignment';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { useNavigate } from 'react-router-dom';
import { API } from '../../../../../../api';
import { useAppSelector } from '../../../../../../redux/hooks';
import { useProgramContext } from '../../../../../../hooks/useProgramContext';
import useCapabilityRelease from '../../../../../../hooks/useCapabilityRelease';
import { secondaryButtonSx } from '../../../../../../components/ui/Buttons/buttonStyles';
import { SETUP_PROGRESS_EVENT } from '../../../../../../components/gamification/useAdminSetupProgress';
import createFrameworkImg from '../../../../../../assets/images/setup-guide-create-framework.png';
import membersImg from '../../../../../../assets/images/members.png';
import reportsImg from '../../../../../../assets/images/reports.png';

/* ─────────────────────────────────────────────────────────────
   Step definitions
───────────────────────────────────────────────────────────── */
const STEPS = [
  'Welcome',
  'Configure',
  'Integrate',
  'Build',
  'Publish',
  'Manage',
  'Measure',
];

interface StepFooterConfig {
  secondary:        string;
  secondaryVariant: 'ghost' | 'action';
  primary:          string;
}

const FOOTER_CONFIG: StepFooterConfig[] = [
  { secondary: 'Resume Later',       secondaryVariant: 'ghost',  primary: 'Get Started' },
  { secondary: 'Configure Settings', secondaryVariant: 'action', primary: 'Next'        },
  { secondary: 'Integrate Knowledge', secondaryVariant: 'action', primary: 'Next'        },
  { secondary: 'Build Program Plan', secondaryVariant: 'action', primary: 'Next' },
  { secondary: 'Publish Programs', secondaryVariant: 'action', primary: 'Next' },
  { secondary: 'Manage People',      secondaryVariant: 'action', primary: 'Next'        },
  { secondary: 'Measure the Impact', secondaryVariant: 'action', primary: 'Done'        },
];

/* ─────────────────────────────────────────────────────────────
   Gradient text helper
───────────────────────────────────────────────────────────── */
const gradientTextSx = {
  background: 'linear-gradient(180deg, #F79420 0%, #F15A26 100%)',
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  backgroundClip: 'text',
};

/* ─────────────────────────────────────────────────────────────
   Shared step scaffold

   Every content step (2-7) is built from these so the type scale,
   spacing, padding and layout stay identical. The rule: copy on the
   left, the step's visual on the right, vertically centred. The copy
   column flexes next to a fixed-width panel (Configure / Integrate /
   Launch) and is a fixed width next to a screenshot that fills the
   rest (Create / Manage / Report), so the three screenshot steps are
   sized identically. Welcome (step 1) is deliberately a centred hero.
───────────────────────────────────────────────────────────── */
const STEP_CONTENT_SX = { flex: 1, minHeight: 0, px: '48px', py: '40px' } as const;
const IMAGE_COPY_WIDTH = 340;

const stepHeadingSx = {
  fontSize: 32,
  fontFamily: 'Outfit, Inter, sans-serif',
  fontWeight: 700,
  lineHeight: 1.15,
  color: '#fff',
} as const;

const stepBodySx = {
  fontSize: 15,
  fontFamily: 'Inter, sans-serif',
  fontWeight: 400,
  color: '#8E8E8E',
  lineHeight: 1.6,
} as const;

// Heading with a gradient lead word, e.g. lead="Create" rest=" Your Framework".
const StepHeading = ({ lead, rest }: { lead: string; rest?: React.ReactNode }) => (
  <Typography sx={stepHeadingSx}>
    <Box component="span" sx={gradientTextSx}>{lead}</Box>
    {rest}
  </Typography>
);

// Copy block: gradient heading + body paragraph, on one consistent rhythm.
const StepCopy = ({ lead, rest, children }: { lead: string; rest?: React.ReactNode; children: React.ReactNode }) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
    <StepHeading lead={lead} rest={rest} />
    <Typography sx={stepBodySx}>{children}</Typography>
  </Box>
);

// The single shared layout. mediaFills=true gives a fixed copy column with a
// screenshot that fills the rest (Create / Manage / Report); otherwise the copy
// flexes beside a fixed-width panel (Configure / Integrate / Launch).
const SplitStep = ({ copy, media, mediaFills }: { copy: React.ReactNode; media: React.ReactNode; mediaFills?: boolean }) => (
  <Box sx={{ ...STEP_CONTENT_SX, display: 'flex', flexDirection: 'row', gap: '48px', alignItems: 'center' }}>
    <Box sx={{ ...(mediaFills ? { width: IMAGE_COPY_WIDTH } : { flex: 1 }), flexShrink: 0, minWidth: 0 }}>
      {copy}
    </Box>
    <Box sx={{ ...(mediaFills ? { flex: 1 } : {}), minWidth: 0, flexShrink: 0, display: 'flex', justifyContent: 'flex-end' }}>
      {media}
    </Box>
  </Box>
);

// Screenshot media, framed identically on every image step.
const StepImage = ({ src, alt }: { src: string; alt: string }) => (
  <Box
    component="img"
    src={src}
    alt={alt}
    sx={{
      width: '100%',
      maxHeight: 300,
      objectFit: 'cover',
      objectPosition: 'top',
      borderRadius: '10px',
      border: '1px solid #2A2A2A',
      display: 'block',
      pointerEvents: 'none',
    }}
  />
);

// Bordered panel that holds a vertical list (Integrate) or a sequence (Launch),
// so those two right-hand visuals read as siblings.
const MediaCard = ({ children }: { children: React.ReactNode }) => (
  <Box
    sx={{
      width: 360,
      flexShrink: 0,
      background: '#161616',
      border: '1px solid #2A2A2A',
      borderRadius: '12px',
      p: '20px',
      display: 'flex',
      flexDirection: 'column',
    }}
  >
    {children}
  </Box>
);

/* ─────────────────────────────────────────────────────────────
   Stepper
───────────────────────────────────────────────────────────── */
const ModalStepper = ({ currentStep, isStepDone, steps }: { currentStep: number; isStepDone: (n: number) => boolean; steps: string[] }) => (
  <Box sx={{ background: '#1A1A1A', px: '32px', py: '24px', flexShrink: 0 }}>
    <Box sx={{ display: 'flex', alignItems: 'center', height: 56 }}>
      {steps.map((label, index) => {
        const stepNum  = index + 1;
        const isActive = stepNum === currentStep;
        const isPast   = stepNum < currentStep;
        const taskDone = isStepDone(stepNum);

        return (
          <React.Fragment key={stepNum}>
            <Box
              sx={{
                flex: '1 0 0',
                minWidth: 0,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '8px',
              }}
            >
              <Box
                sx={{
                  width: 28,
                  height: 28,
                  borderRadius: '14px',
                  flexShrink: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  position: 'relative',
                  ...(isActive || isPast
                    ? { background: 'linear-gradient(180deg, #F79420 0%, #F15A26 100%)' }
                    : { border: '1px solid #3A3A3A' }),
                }}
              >
                <Typography
                  sx={{
                    fontSize: 12,
                    fontFamily: 'Outfit, Inter, sans-serif',
                    fontWeight: 700,
                    color: isActive || isPast ? '#fff' : '#6B6B6B',
                    lineHeight: 1,
                  }}
                >
                  {stepNum}
                </Typography>

                {taskDone && (
                  <Box
                    sx={{
                      position: 'absolute',
                      top: -3,
                      right: -3,
                      width: 13,
                      height: 13,
                      background: '#00972D',
                      borderRadius: '6.5px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      overflow: 'hidden',
                    }}
                  >
                    <Typography sx={{ fontSize: 9, fontFamily: 'Inter', fontWeight: 700, color: '#fff', lineHeight: 1, userSelect: 'none' }}>
                      ✓
                    </Typography>
                  </Box>
                )}
              </Box>

              <Typography
                sx={{
                  fontSize: 11,
                  fontFamily: 'Outfit, Inter, sans-serif',
                  fontWeight: isActive ? 700 : 600,
                  color: isActive ? '#fff' : '#6B6B6B',
                  whiteSpace: 'nowrap',
                  lineHeight: 1,
                }}
              >
                {label}
              </Typography>
            </Box>

            {index < steps.length - 1 && (
              <Box
                sx={{
                  flex: '1 0 0',
                  minWidth: 0,
                  height: 28,
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                <Box sx={{ width: '100%', height: '1px', background: '#2A2A2A' }} />
              </Box>
            )}
          </React.Fragment>
        );
      })}
    </Box>
  </Box>
);

/* ─────────────────────────────────────────────────────────────
   Footer
───────────────────────────────────────────────────────────── */
interface FooterProps {
  currentStep:   number;
  onBack:        () => void;
  onSecondary:   () => void;
  onPrimary:     () => void;
  isStepDone:    (n: number) => boolean;
  footerConfigs: StepFooterConfig[];
  onReturnToElliott?: () => void;
}

const ModalFooter = ({ currentStep, onBack, onSecondary, onPrimary, isStepDone, footerConfigs, onReturnToElliott }: FooterProps) => {
  const config    = footerConfigs[currentStep - 1];
  const stepDone  = config.secondaryVariant === 'action' && isStepDone(currentStep);

  return (
    <Box
      sx={{
        borderTop: '1px solid #2A2A2A',
        px: '32px',
        py: '24px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexShrink: 0,
      }}
    >
      {/* Left: Previous (step > 1) + Return to Elliott + step counter */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
        {currentStep > 1 && (
          <Box component="button" onClick={onBack} sx={{ ...secondaryButtonSx, fontSize: 14, cursor: 'pointer' }}>
            Previous
          </Box>
        )}
        {onReturnToElliott && (
          <Box
            component="button"
            onClick={onReturnToElliott}
            sx={{
              p: 0,
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              color: '#F79420',
              fontFamily: 'Inter, sans-serif',
              fontSize: 13,
              fontWeight: 500,
              '&:hover': { textDecoration: 'underline' }
            }}
          >
            Return to Elliott
          </Box>
        )}
        <Typography
          sx={{
            fontSize: 12,
            fontFamily: 'Inter, sans-serif',
            fontWeight: 500,
            color: '#8E8E8E',
            whiteSpace: 'nowrap',
          }}
        >
          Step {currentStep} of {STEPS.length}
        </Typography>
      </Box>

      {/* Buttons */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
        {/* Secondary — ghost (Step 1) or the step's action (Steps 2–7) */}
        {config.secondaryVariant === 'ghost' ? (
          <Box
            component="button"
            onClick={onSecondary}
            sx={{
              px: '16px',
              py: '12px',
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <Typography
              sx={{
                fontSize: 14,
                fontFamily: 'Inter, sans-serif',
                fontWeight: 500,
                color: '#8E8E8E',
                whiteSpace: 'nowrap',
                transition: 'color 0.15s',
                '&:hover': { color: '#FDFDFD' },
              }}
            >
              {config.secondary}
            </Typography>
          </Box>
        ) : (
          <Box
            component="button"
            onClick={onSecondary}
            sx={{
              px: '16px',
              py: '12px',
              position: 'relative',
              background: stepDone ? '#2A2A2A' : '#1C1C1C',
              borderRadius: '8px',
              border: stepDone ? '1px solid #3A3A3A' : '1px solid #2A2A2A',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              transition: 'opacity 0.15s',
              '&:hover': { opacity: 0.9 },
            }}
          >
            <Typography
              sx={{
                fontSize: 14,
                fontFamily: 'Inter, sans-serif',
                fontWeight: 500,
                color: stepDone ? '#6B6B6B' : '#fff',
                whiteSpace: 'nowrap',
              }}
            >
              {config.secondary}
            </Typography>
            {stepDone && (
              <Box sx={{ position: 'absolute', top: -5, right: -5, width: 13, height: 13, background: '#00972D', borderRadius: '6.5px', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
                <Typography sx={{ fontSize: 9, fontFamily: 'Inter', fontWeight: 700, color: '#fff', lineHeight: 1, userSelect: 'none' }}>✓</Typography>
              </Box>
            )}
          </Box>
        )}

        {/* Primary CTA — orange on every step */}
        <Box
          component="button"
          onClick={onPrimary}
          sx={{
            px: '24px',
            py: '12px',
            background: 'linear-gradient(180deg, #F79420 0%, #F15A26 100%)',
            borderRadius: '8px',
            border: 'none',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            transition: 'opacity 0.15s',
            '&:hover': { opacity: 0.9 },
          }}
        >
          <Typography
            sx={{
              fontSize: 14,
              fontFamily: 'Inter, sans-serif',
              fontWeight: 600,
              color: '#fff',
              whiteSpace: 'nowrap',
            }}
          >
            {config.primary}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

/* ─────────────────────────────────────────────────────────────
   Step 1 — Welcome
───────────────────────────────────────────────────────────── */
const WelcomeBody = () => (
  <Box
    sx={{
      flex: 1,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '12px',
      px: '80px',
      pt: '64px',
      pb: '80px',
    }}
  >
    {/* "YOUR GUIDED SETUP" */}
    <Typography
      sx={{
        fontSize: 14,
        fontFamily: 'Outfit, Inter, sans-serif',
        fontWeight: 700,
        textTransform: 'uppercase',
        letterSpacing: '0.04em',
        textAlign: 'center',
        ...gradientTextSx,
      }}
    >
      Your guided setup
    </Typography>

    {/* "I'm Elliott, Welcome to Dosen" */}
    <Typography
      sx={{
        fontSize: 36,
        fontFamily: 'Outfit, Inter, sans-serif',
        fontWeight: 700,
        color: '#fff',
        textAlign: 'center',
        lineHeight: 1.2,
      }}
    >
      {`I'm `}
      <Box component="span" sx={gradientTextSx}>Elliott</Box>
      {`. Welcome to Dosen.`}
    </Typography>

    {/* Body copy */}
    <Box sx={{ pt: '12px', width: '100%' }}>
      <Typography
        sx={{
          fontSize: 16,
          fontFamily: 'Inter, sans-serif',
          fontWeight: 400,
          color: '#8E8E8E',
          textAlign: 'center',
          lineHeight: 1.6,
        }}
      >
        I build and run your Program Plan, train your teams, report Capability Gaps that put your teams at risk, close
        those gaps with informed recommendations, and keep you report-ready with real-time insights throughout it all.
        In other words, you set the standards, I ensure they&apos;re met.
      </Typography>
    </Box>

    {/* Setup instruction with inline i icon */}
    <Box sx={{ pt: '8px', width: '100%' }}>
      <Typography
        sx={{
          fontSize: 16,
          fontFamily: 'Inter, sans-serif',
          fontWeight: 400,
          color: '#8E8E8E',
          textAlign: 'center',
          lineHeight: 1.6,
        }}
      >
        First, let's get a few essentials in place. This quick guide walks you through each step.{' '}
        <Box
          component="span"
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 18,
            height: 18,
            borderRadius: '50%',
            background: 'linear-gradient(180deg, #F79420 0%, #F15A26 100%)',
            verticalAlign: 'middle',
            position: 'relative',
            top: '-1px',
            mx: '3px',
            flexShrink: 0,
          }}
        >
          <Box component="span" sx={{ fontSize: 11, fontFamily: 'Inter', fontWeight: 700, color: '#fff', lineHeight: 1, userSelect: 'none' }}>
            i
          </Box>
        </Box>
      </Typography>
    </Box>
  </Box>
);

/* ─────────────────────────────────────────────────────────────
   Step 2 — Configure: nav mockup
───────────────────────────────────────────────────────────── */
const NAV_ITEMS = ['DASHBOARD', 'PEOPLE', 'KNOWLEDGE', 'INSIGHTS', 'INTEGRATIONS'];

const NavMockup = () => (
  <Box
    sx={{
      width: 228,
      flexShrink: 0,
      background: '#111111',
      borderRadius: '10px',
      border: '1px solid #2A2A2A',
      overflow: 'hidden',
      boxShadow: '0px 8px 32px rgba(0,0,0,0.4)',
    }}
  >
    {/* Learner / Admin toggle */}
    <Box sx={{ p: '12px 12px 8px' }}>
      <Box
        sx={{
          background: '#1C1C1C',
          borderRadius: '100px',
          p: '3px',
          display: 'flex',
          border: '1px solid #2A2A2A',
        }}
      >
        <Box sx={{ flex: 1, py: '5px', display: 'flex', justifyContent: 'center', borderRadius: '100px' }}>
          <Typography sx={{ color: '#868686', fontSize: 11, fontFamily: 'Inter', fontWeight: 500 }}>
            Learner
          </Typography>
        </Box>
        <Box
          sx={{
            flex: 1,
            py: '5px',
            display: 'flex',
            justifyContent: 'center',
            borderRadius: '100px',
            background: 'linear-gradient(180deg, #F79420 0%, #F15A26 100%)',
          }}
        >
          <Typography sx={{ color: '#fff', fontSize: 11, fontFamily: 'Inter', fontWeight: 600 }}>
            Admin
          </Typography>
        </Box>
      </Box>
    </Box>

    {/* A divider line between the toggle and the admin nav */}
    <Box sx={{ mx: '14px', mt: '6px', mb: '4px', height: '1px', background: '#2A2A2A' }} />

    {/* Nav items */}
    {NAV_ITEMS.map(item => (
      <Box key={item} sx={{ px: '14px', py: '9px' }}>
        <Typography
          sx={{
            color: '#FDFDFD',
            fontSize: 11,
            fontFamily: 'Inter',
            fontWeight: 700,
            letterSpacing: '0.04em',
          }}
        >
          {item}
        </Typography>
      </Box>
    ))}

    {/* SETTINGS — highlighted as active */}
    <Box sx={{ mx: '6px', mb: '8px', mt: '2px', px: '8px', py: '9px', background: '#2A2A2A', borderRadius: '6px' }}>
      <Typography
        sx={{
          color: '#FDFDFD',
          fontSize: 11,
          fontFamily: 'Inter',
          fontWeight: 700,
          letterSpacing: '0.04em',
        }}
      >
        SETTINGS
      </Typography>
    </Box>
  </Box>
);

/* ─────────────────────────────────────────────────────────────
   Step 2 — Configure
───────────────────────────────────────────────────────────── */
const ConfigureBody = () => (
  <SplitStep
    copy={
      <StepCopy lead="Configure" rest=" settings">
        Set your organization’s core preferences, so I can organize your programs, people and reporting the right way
        from the start.
      </StepCopy>
    }
    media={<NavMockup />}
  />
);

/* ─────────────────────────────────────────────────────────────
   Step 3 — Integrate
───────────────────────────────────────────────────────────── */
interface IntegrationItemProps {
  icon: React.ReactNode;
  title: string;
  description: string;
}

const IntegrationRow = ({ icon, title, description }: IntegrationItemProps) => (
  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: '14px' }}>
    <Box
      sx={{
        width: 40,
        height: 40,
        borderRadius: '20px',
        background: 'linear-gradient(180deg, #F79420 0%, #F15A26 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
      }}
    >
      {icon}
    </Box>
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '2px', minWidth: 0 }}>
      <Typography
        sx={{ fontSize: 15, fontFamily: 'Outfit, Inter, sans-serif', fontWeight: 700, color: '#fff' }}
      >
        {title}
      </Typography>
      <Typography
        sx={{ fontSize: 13, fontFamily: 'Inter, sans-serif', fontWeight: 400, color: '#8E8E8E', lineHeight: 1.4 }}
      >
        {description}
      </Typography>
    </Box>
  </Box>
);

const INTEGRATION_ITEMS: IntegrationItemProps[] = [
  {
    icon:        <MenuBookIcon sx={{ fontSize: 20, color: '#fff' }} />,
    title:       'Knowledge',
    description: 'Sync your latest docs to power learning creation',
  },
  {
    icon:        <NotificationsNoneIcon sx={{ fontSize: 20, color: '#fff' }} />,
    title:       'Notifications',
    description: 'Push notifications into Teams and daily tools',
  },
  {
    icon:        <PeopleIcon sx={{ fontSize: 20, color: '#fff' }} />,
    title:       'Onboarding',
    description: 'Onboard people in clicks with your org data',
  },
];

const IntegrateBody = () => (
  <SplitStep
    copy={
      <StepCopy lead="Integrate" rest=" your Knowledge">
        From the same menu, open{' '}
        <Box component="span" sx={{ fontStyle: 'italic' }}>Integrations</Box>
        {' '}to connect your knowledge and communication tools. I'll build learning from trusted
        company context and deliver it where your teams already work.
      </StepCopy>
    }
    media={
      <MediaCard>
        {INTEGRATION_ITEMS.map((item, i) => (
          <Box key={item.title} sx={{ mt: i === 0 ? 0 : '20px' }}>
            <IntegrationRow {...item} />
          </Box>
        ))}
      </MediaCard>
    }
  />
);

/* ─────────────────────────────────────────────────────────────
   Step 4 — Create
───────────────────────────────────────────────────────────── */
const CreateBody = () => (
  <SplitStep
    mediaFills
    copy={
      <StepCopy lead="Build" rest=" your Program Plan">
        Tell me what your teams need to be able to do. I’ll turn your priorities into a clear plan of programs to build,
        publish and measure.
      </StepCopy>
    }
    media={<StepImage src={createFrameworkImg} alt="Program Plan" />}
  />
);

/* ─────────────────────────────────────────────────────────────
   Step 5 — Publish
───────────────────────────────────────────────────────────── */
const PUBLISH_WORKFLOW = [
  { icon: <AssignmentIcon sx={{ fontSize: 16, color: '#fff' }} />,         label: 'I gather your requirements'       },
  { icon: <MenuBookIcon sx={{ fontSize: 16, color: '#fff' }} />, label: 'I generate your learning courses' },
  {
    icon: <CheckCircleOutlineIcon sx={{ fontSize: 16, color: '#fff' }} />,
    label: 'You review and approve the courses'
  },
  { icon: <PeopleIcon sx={{ fontSize: 16, color: '#fff' }} />, label: 'I enroll learners automatically' }
];

const PublishBody = () => (
  <SplitStep
    copy={
      <StepCopy lead="Publish" rest=" your programs">
        Set the purpose, audience, and goals for each program. I'll generate and structure an interactive learning
        experience, ready to assign to the right people.
      </StepCopy>
    }
    media={
      <MediaCard>
        {PUBLISH_WORKFLOW.map((step, i) => (
          <React.Fragment key={step.label}>
            <Box sx={{ height: '44px', display: 'flex', alignItems: 'center' }}>
              <Box
                sx={{
                  width: 28,
                  height: 28,
                  borderRadius: '8px',
                  background: 'linear-gradient(180deg, #F79420 0%, #F15A26 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                {step.icon}
              </Box>
              <Typography
                sx={{
                  flex: '1 0 0',
                  fontSize: 13,
                  fontFamily: 'Inter, sans-serif',
                  fontWeight: 400,
                  color: '#8E8E8E',
                  lineHeight: 1.4,
                  textAlign: 'center',
                }}
              >
                {step.label}
              </Typography>
            </Box>

            {i < PUBLISH_WORKFLOW.length - 1 && (
              <Box sx={{ height: '24px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <KeyboardArrowDownIcon sx={{ fontSize: 16, color: '#8E8E8E' }} />
              </Box>
            )}
          </React.Fragment>
        ))}
      </MediaCard>
    }
  />
);

/* ─────────────────────────────────────────────────────────────
   Step 6 — Manage
───────────────────────────────────────────────────────────── */
const ManageBody = () => (
  <SplitStep
    mediaFills
    copy={
      <StepCopy lead="Manage" rest=" your people">
        Track progress, performance, and engagement across every program. People shows who's active, who needs support,
        and how people are tracking against your goals.
      </StepCopy>
    }
    media={<StepImage src={membersImg} alt="People" />}
  />
);

/* ─────────────────────────────────────────────────────────────
   Step 7 — Report
───────────────────────────────────────────────────────────── */
const ReportBody = () => {
  // Capability and Activity are the release's measures; other orgs keep the earlier line.
  const capabilityRelease = useCapabilityRelease();
  return (
    <SplitStep
      mediaFills
      copy={
        <StepCopy lead="Measure" rest=" the impact">
          {capabilityRelease
            ? 'See Capability and Activity across your organization in real time. Spot gaps, measure progress and export the data you need to show impact.'
            : 'See learning performance across your organization in real time. Track KPIs, spot gaps, measure progress, and export the data you need to prove impact.'}
        </StepCopy>
      }
      media={<StepImage src={reportsImg} alt="Insights dashboard" />}
    />
  );
};

const STEP_BODIES: React.FC<{ step: number }>[] = [
  ()       => <WelcomeBody />,
  ()       => <ConfigureBody />,
  ()       => <IntegrateBody />,
  ()       => <CreateBody />,
  ()       => <PublishBody />,
  ()       => <ManageBody />,
  ()       => <ReportBody />,
];

/* ─────────────────────────────────────────────────────────────
   Program Admin (groupAdmin) step subset — steps 1, 5, 6, 7
   mapped to a clean 1-4 local index
───────────────────────────────────────────────────────────── */
// 0-based indices into STEPS / FOOTER_CONFIG / STEP_BODIES
const PA_STEP_INDICES = [0, 4, 5, 6];
const PA_STEP_LABELS = ['Welcome', 'Publish', 'Manage', 'Measure'];

// Optional (original) steps: still shown and still tickable, but not required
// for setup to count as complete. SharePoint (step 3) is an integration many
// orgs never use, so gating completion on it left the guide permanently
// "unfinished". Keep this in sync with useAdminSetupProgress.OPTIONAL_ORIGINAL_STEPS.
const OPTIONAL_ORIGINAL_STEPS = new Set<number>([3]);

/* ─────────────────────────────────────────────────────────────
   Auth headers helper (mirrors useAdminLearningLifecycle)
───────────────────────────────────────────────────────────── */
const getAuthHeaders = () => {
  try {
    const tokens = JSON.parse(localStorage.getItem('tokens') || '{}');
    return { idtoken: tokens.id_token || '', authorization: tokens.access_token || '' };
  } catch {
    return { idtoken: '', authorization: '' };
  }
};

/* ─────────────────────────────────────────────────────────────
   Modal
───────────────────────────────────────────────────────────── */
interface SetupGuideModalProps {
  open:     boolean;
  onClose:  () => void;
  variant?: 'orgAdmin' | 'groupAdmin';
  // When set, shows a "Return to Elliott" CTA that hands back to the chat modal
  // (so the Setup Guide appears as part of the same Elliott flow).
  onReturnToElliott?: () => void;
}

const SetupGuideModal = ({ open, onClose, variant = 'orgAdmin', onReturnToElliott }: SetupGuideModalProps) => {
  const [currentStep, setCurrentStep] = useState(1);
  const navigate = useNavigate();

  // Context / store data needed for completion checks + routing
  const { orgId, coId, location, isPlatformAdmin } = useProgramContext();
  const orgSettings  = useAppSelector((state: any) => state.orgDetails.data?.orgSettings ?? false);
  const authOrgId    = useAppSelector((state: any) => state.orgDetails.data?.authId ?? '');

  // Variant-derived config — stable for the lifetime of the component instance
  const isGroupAdmin      = variant === 'groupAdmin';
  const activeStepCount   = isGroupAdmin ? 4 : STEPS.length;
  const activeStepLabels  = isGroupAdmin ? PA_STEP_LABELS : STEPS;
  const activeFooterConfs = isGroupAdmin ? PA_STEP_INDICES.map(i => FOOTER_CONFIG[i]) : FOOTER_CONFIG;
  const manualDoneKey     = isGroupAdmin ? `dosen_setup_done_grp_${orgId}` : `dosen_setup_done_${orgId}`;
  // Maps local step (1-N) → original step number (1-7)
  const getOriginalStep = (localStep: number) =>
    isGroupAdmin ? PA_STEP_INDICES[localStep - 1] + 1 : localStep;

  // Steps 1, 6, 7 complete when the user proceeds through them (persisted per org)
  const [manualDone, setManualDone] = useState<Set<number>>(new Set());

  // API-driven completion for steps 3–5
  const [isSharePointConnected, setIsSharePointConnected] = useState(false);
  const [programCount,          setProgramCount]          = useState(0);
  const [hasLaunchedProgram,    setHasLaunchedProgram]    = useState(false);

  // Fetch API-driven completion data as soon as orgId/tokens are available —
  // not on modal open, so ticks are ready before the user even opens the modal.
  useEffect(() => {
    if (!orgId) return;

    if (!isGroupAdmin) {
      API.getSharePointConnection(orgId)
        .then(data => setIsSharePointConnected(data.isConnected === true))
        .catch(() => setIsSharePointConnected(false));
    }

    const { idtoken, authorization } = getAuthHeaders();

    if (isGroupAdmin) {
      // Group admin: check only programs belonging to this user
      API.getAllPrograms({ orgId })
        .then(res => {
          const programs: any[] = Array.isArray(res?.data?.programs) ? res.data.programs
            : Array.isArray(res?.data) ? res.data : [];
          setHasLaunchedProgram(programs.some((p: any) =>
            p.status === 'invited' || p.status === 'active'
          ));
        })
        .catch(() => setHasLaunchedProgram(false));
    } else if (idtoken && authorization) {
      // Platform admins must pass orgId as a query param (the BE infers org
      // from JWT only for org/group admins); without it the v3 endpoint 403s.
      API.getV3AdminPrograms({ orgId: isPlatformAdmin ? orgId : undefined, idtoken, authorization, location })
        .then(res => {
          const programs: any[] = Array.isArray(res?.data?.programs) ? res.data.programs : [];
          setProgramCount(programs.length);
          setHasLaunchedProgram(programs.some((p: any) =>
            p.status === 'invited' || p.status === 'active'
          ));
        })
        .catch(() => { setProgramCount(0); setHasLaunchedProgram(false); });
    }
  }, [orgId, isGroupAdmin, isPlatformAdmin, location]);

  // On modal open: reset step + restore manual completions from localStorage
  useEffect(() => {
    if (!open) return;
    setCurrentStep(1);
    // Wait for orgId: until it resolves, manualDoneKey points at a
    // `..._undefined` bucket, and restoring from it would wipe the Manage /
    // Report ticks the admin has already earned under the real key.
    if (!orgId) return;
    try {
      const stored = localStorage.getItem(manualDoneKey);
      setManualDone(new Set(stored ? JSON.parse(stored) : []));
    } catch { setManualDone(new Set()); }
  }, [open, manualDoneKey, orgId]);

  const isStepDone = (localStep: number): boolean => {
    const orig = getOriginalStep(localStep);
    switch (orig) {
      case 1: return manualDone.has(localStep);
      case 2: return orgSettings === true;
      case 3: return isSharePointConnected;
      case 4: return programCount > 0;
      case 5: return hasLaunchedProgram;
      case 6: return manualDone.has(localStep);
      case 7: return manualDone.has(localStep);
      default: return false;
    }
  };

  // Write "all complete" flag so the dashboard knows to stop auto-launching.
  // Optional steps (e.g. SharePoint) don't block completion; only required
  // steps must be done.
  useEffect(() => {
    if (!orgId) return;
    const allDone = Array.from({ length: activeStepCount }, (_, i) => i + 1)
      .filter(localStep => !OPTIONAL_ORIGINAL_STEPS.has(getOriginalStep(localStep)))
      .every(isStepDone);
    if (allDone) {
      const key = isGroupAdmin ? `dosen_setup_complete_grp_${orgId}` : `dosen_setup_complete_${orgId}`;
      localStorage.setItem(key, 'true');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [manualDone, isSharePointConnected, programCount, hasLaunchedProgram, orgSettings, orgId]);

  if (!open) return null;

  const StepBody = STEP_BODIES[isGroupAdmin ? PA_STEP_INDICES[currentStep - 1] : currentStep - 1];

  const markManualDone = (localStep: number) => {
    try {
      const existing: number[] = JSON.parse(localStorage.getItem(manualDoneKey) || '[]');
      if (!existing.includes(localStep)) {
        localStorage.setItem(manualDoneKey, JSON.stringify([...existing, localStep]));
        // The storage event never reaches this tab: tell the progress chip / badge directly.
        window.dispatchEvent(new Event(SETUP_PROGRESS_EVENT));
      }
    } catch {}
    setManualDone(prev => { const s = new Set(prev); s.add(localStep); return s; });
  };

  const handlePrimary = () => {
    if (currentStep === 1) markManualDone(1);
    if (currentStep < activeStepCount) {
      setCurrentStep(s => s + 1);
    } else {
      onClose();
    }
  };

  const handleBack = () => {
    if (currentStep > 1) setCurrentStep(s => s - 1);
  };

  const handleSecondary = () => {
    const config = activeFooterConfs[currentStep - 1];
    if (config.secondaryVariant === 'ghost') { onClose(); return; }

    // Mark done when the CTA click is the completion trigger (Manage + Report steps)
    const orig = getOriginalStep(currentStep);
    if (orig === 6 || orig === 7) markManualDone(currentStep);

    const orgAdminRoutes: Record<number, string> = {
      2: '/app/orgsettings',
      3: '/app/integrations',
      4: `/app/programdetails/${orgId}/${coId}/admin/overview`,
      5: `/app/programdetails/${orgId}/${coId}/admin/overview`,
      6: `/app/org/${authOrgId}/members`,
      7: `/app/programdetails/${orgId}/${coId}/admin/insights-v3`,
    };
    const groupAdminRoutes: Record<number, string> = {
      2: `/app/programdetails/${orgId}/${coId}/admin/overview`,
      3: `/app/org/${authOrgId}/members`,
      4: `/app/programdetails/${orgId}/${coId}/admin/insights-v3`,
    };
    const route = (isGroupAdmin ? groupAdminRoutes : orgAdminRoutes)[currentStep];
    if (route) { onClose(); navigate(route); }
  };

  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) onClose();
  };

  return (
    <Box
      onClick={handleBackdropClick}
      sx={{
        position: 'fixed',
        inset: 0,
        zIndex: 1400,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'rgba(0, 0, 0, 0.75)',
        backdropFilter: 'blur(6px)',
        WebkitBackdropFilter: 'blur(6px)',
        // Eases in over the dashboard rather than appearing all at once.
        animation: 'setupGuideBackdropIn 0.25s ease both',
        '@keyframes setupGuideBackdropIn': { from: { opacity: 0 }, to: { opacity: 1 } }
      }}
    >
      {/* Modal card — matched to the Elliott chat modal footprint so the Setup
          Guide appears as part of the same flow. */}
      <Box
        sx={{
          width: 1040,
          height: 'min(88vh, 720px)',
          maxWidth: '95vw',
          maxHeight: '95vh',
          background: '#1A1A1A',
          border: '1px solid #2A2A2A',
          borderRadius: '12px',
          boxShadow: '0px 32px 64px -12px rgba(0,0,0,0.5)',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          animation: 'setupGuideCardIn 0.3s ease both',
          '@keyframes setupGuideCardIn': {
            from: { opacity: 0, transform: 'translateY(8px) scale(0.99)' },
            to: { opacity: 1, transform: 'none' }
          }
        }}
      >
        <ModalStepper currentStep={currentStep} isStepDone={isStepDone} steps={activeStepLabels} />

        <Box sx={{ width: '100%', height: '1px', background: '#2A2A2A', flexShrink: 0 }} />

        <StepBody step={currentStep} />

        <ModalFooter
          currentStep={currentStep}
          onBack={handleBack}
          onSecondary={handleSecondary}
          onPrimary={handlePrimary}
          isStepDone={isStepDone}
          footerConfigs={activeFooterConfs}
          onReturnToElliott={onReturnToElliott}
        />
      </Box>
    </Box>
  );
};

export default SetupGuideModal;
